<!--
  AchBillingCard -- Distributor App > Account > Payments.

  Unit 5 of the Stripe payments/payouts plan (requirements ON-02, ON-03,
  NET-06). Lets a distributor authorize ACH Direct Debit so ThreadFlow can
  collect the 1st/15th invoices (decoration, plus a Net Checkout order's
  platform fee and shipping).

  WHICH RELATIONSHIP THIS IS
  ==========================
  This card sits next to StripeConnectCard and they are easy to confuse, so:

    - StripeConnectCard = how the DISTRIBUTOR GETS PAID by shoppers (Stripe
      Connect, a connected account).
    - AchBillingCard (this) = how THREADFLOW BILLS THE DISTRIBUTOR (a
      platform-side Stripe Customer with a us_bank_account payment method).

  They are adjacent on purpose -- the ACH design doc asks for one continuous
  "Payments Setup" session so a distributor does not discover weeks later that
  a second administrative step blocks Net Checkout. The underlying Stripe
  objects stay distinct; only the presentation is unified.

  NO CARD-FALLBACK SURCHARGE
  ==========================
  ACH is presented as preferred because it is cheaper for us, but paying by
  card is an ordinary secondary option at the SAME price. NET-06 and BR-11
  retire the automatic +4% card-fallback surcharge; this card must never
  reintroduce it, in copy or in computation.

  VERIFICATION PATHS
  ==================
  Stripe decides which one a given bank gets. Instant verification (Financial
  Connections) returns a succeeded SetupIntent immediately. The micro-deposit
  fallback leaves it in `requires_action` for a couple of business days. The
  bank dialog (AchBankDialog) reports that as "pending" rather than an error,
  because nothing has gone wrong; this card shows it from either that signal
  (`verificationPending`) or the server's `pending_verification` status.
  All sentences live in achCopy.js.
-->
<template>
  <v-card class="ach-billing-card" data-testid="ach-billing-card">
    <v-card-title>
      <h2>{{ copy.cardTitle }}</h2>
    </v-card-title>

    <v-card-text>
      <p class="ach-lede" data-testid="ach-lede">{{ copy.lede }}</p>

      <!-- Active: a mandate is on file. -->
      <div v-if="isActive" class="ach-summary" data-testid="ach-status-active">
        <span class="ach-badge ach-badge--active">{{
          copy.badgeActive
        }}</span>
        <p class="ach-bank">
          {{ bankLabel }}
        </p>
        <p
          v-if="acceptedAtLabel"
          class="ach-meta"
          data-testid="ach-authorized-on"
        >
          {{ copy.authorizedOn }} {{ acceptedAtLabel }}
        </p>
      </div>

      <!-- Micro-deposit verification pending: not an error state. Comes from
           the server (pending_verification) or from a setup the dialog just
           reported as pending. -->
      <div
        v-else-if="isPending"
        class="ach-summary"
        data-testid="ach-status-pending"
      >
        <span class="ach-badge ach-badge--pending">{{ copy.badgePending }}</span>
        <p
          class="ach-meta ach-meta--pending"
          data-testid="ach-verification-pending"
        >
          {{ copy.pending }}
        </p>
      </div>

      <!-- Verification failed: say so, and the button below retries. -->
      <div
        v-else-if="isFailed"
        class="ach-summary"
        data-testid="ach-status-failed"
      >
        <span class="ach-badge ach-badge--failed">{{ copy.badgeFailed }}</span>
        <p class="ach-meta ach-meta--failed">{{ copy.failed }}</p>
      </div>

      <!-- Stripe could not be asked: ``status`` is a fallback, not a fact, so
           never claim "not set up". -->
      <div
        v-else-if="isUnavailable"
        class="ach-summary"
        data-testid="ach-status-unavailable"
      >
        <span class="ach-badge">{{ copy.badgeUnavailable }}</span>
        <p class="ach-meta">{{ copy.statusUnavailable }}</p>
        <button
          type="button"
          class="tf-btn tf-btn--primary"
          data-testid="ach-status-retry"
          @click="$emit('refresh-status')"
        >
          {{ copy.statusRetryButton }}
        </button>
      </div>

      <!-- Not set up yet (also: any status value we do not recognize). -->
      <div v-else class="ach-summary" data-testid="ach-status-not-set-up">
        <span class="ach-badge">{{ copy.badgeNotSetUp }}</span>
        <p class="ach-meta">{{ copy.notSetUp }}</p>
      </div>

      <p v-if="errorMessage" class="ach-error" data-testid="ach-error">
        {{ errorMessage }}
      </p>
    </v-card-text>

    <v-card-actions>
      <button
        type="button"
        class="tf-btn tf-btn--primary"
        data-testid="ach-setup-btn"
        :disabled="busy || isUnavailable"
        @click="startSetup"
      >
        {{ isActive ? copy.updateButton : copy.setupButton }}
      </button>
    </v-card-actions>
  </v-card>
</template>

<script>
import { computed, ref } from "vue";
import { createAchSetupIntent } from "@/services/billingService";
import { ACH_COPY } from "./achCopy";

export default {
  name: "AchBillingCard",

  props: {
    // The GET /distributor/billing/ach payload, or null while unresolved.
    ach: {
      type: Object,
      default: null,
    },
    // The bank dialog just finished with verification still running (the
    // micro-deposit path). Lets the card tell the truth before the server
    // reports pending_verification itself.
    verificationPending: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["setup-started", "refresh-status"],

  setup(props, { emit }) {
    const busy = ref(false);
    const errorMessage = ref("");

    const status = computed(() => (props.ach ? props.ach.status : null));
    const isActive = computed(() => status.value === "active");
    // Unknown or absent status values fall through to "not set up".
    const isPending = computed(
      () =>
        !isActive.value &&
        (props.verificationPending || status.value === "pending_verification")
    );
    // GET /ach could not reach Stripe. An active mandate is a local fact and
    // always wins; otherwise a pending/failed signal is still better than
    // "unknown".
    const isUnavailable = computed(
      () =>
        !!props.ach &&
        props.ach.status_unavailable === true &&
        !isActive.value &&
        !isPending.value &&
        status.value !== "failed"
    );
    const isFailed = computed(
      () => !isActive.value && !isPending.value && status.value === "failed"
    );

    // "STRIPE TEST BANK ending in 6789", degrading gracefully when Stripe did
    // not return the bank name (the server records the mandate regardless --
    // display metadata must never block an authorization).
    const bankLabel = computed(() => {
      const a = props.ach;
      if (!a) return "";
      const name = a.bank_name;
      const last4 = a.bank_last4;
      if (name && last4) return `${name} ending in ${last4}`;
      if (last4) return `Bank account ending in ${last4}`;
      return "Bank account on file";
    });

    const acceptedAtLabel = computed(() => {
      const a = props.ach;
      if (!a || !a.mandate_accepted_at) return "";
      const d = new Date(a.mandate_accepted_at);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString();
    });

    // Ask the server for a us_bank_account SetupIntent and hand the client
    // secret upward. The parent owns the Stripe.js collection step, so this
    // card stays a presentation component with one network call.
    async function startSetup() {
      if (busy.value) return;
      busy.value = true;
      errorMessage.value = "";
      try {
        const res = await createAchSetupIntent();
        const data = res && res.data ? res.data : null;
        if (!data || !data.client_secret) {
          errorMessage.value = ACH_COPY.setupStartError;
          return;
        }
        emit("setup-started", data);
      } catch (e) {
        errorMessage.value = ACH_COPY.setupStartError;
      } finally {
        busy.value = false;
      }
    }

    return {
      copy: ACH_COPY,
      busy,
      errorMessage,
      isActive,
      isPending,
      isFailed,
      isUnavailable,
      bankLabel,
      acceptedAtLabel,
      startSetup,
    };
  },
};
</script>

<style scoped>
.ach-billing-card {
  padding: var(--tf-sp-2, 8px);
}
.ach-lede {
  font-size: var(--tf-size-body, 1rem);
  color: var(--tf-fg-3, #56636e);
}
.ach-summary {
  margin-top: var(--tf-sp-3, 12px);
}
.ach-badge {
  display: inline-block;
  border-radius: var(--tf-radius-sm, 8px);
  padding: 2px 10px;
  font-size: var(--tf-size-caption, 0.85rem);
  background: var(--tf-bg-2, #eef1f4);
  color: var(--tf-fg-3, #56636e);
}
.ach-badge--active {
  background: var(--tf-success-bg, #e6f4ea);
  color: var(--tf-success, #1e7e34);
}
.ach-bank {
  margin-top: 6px;
  font-size: var(--tf-size-body, 1rem);
}
.ach-meta {
  margin-top: 4px;
  font-size: var(--tf-size-caption, 0.85rem);
  color: var(--tf-fg-3, #56636e);
}
.ach-meta--pending {
  color: var(--tf-warning, #8a6d1f);
}
.ach-badge--pending {
  background: var(--tf-warning-bg, #fdf3d8);
  color: var(--tf-warning, #8a6d1f);
}
.ach-badge--failed {
  background: var(--tf-error-bg, #fbe9e7);
  color: var(--tf-error, #b3261e);
}
.ach-meta--failed {
  color: var(--tf-error, #b3261e);
}
.ach-error {
  margin-top: var(--tf-sp-2, 8px);
  font-size: var(--tf-size-caption, 0.85rem);
  color: var(--tf-error, #b3261e);
}
.tf-btn {
  border: none;
  border-radius: var(--tf-radius-sm, 8px);
  padding: var(--tf-sp-2, 8px) var(--tf-sp-5, 20px);
  font-size: var(--tf-size-body, 1rem);
  cursor: pointer;
}
.tf-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.tf-btn--primary {
  background: var(--tf-primary, #1f6feb);
  color: var(--tf-fg-on-primary, #fff);
}
</style>
